import { getDataTypeForSystemGeneratedColumn, parseSystemColumnFieldId } from '@doss/common-all'
import { MenuItem, Stack, Typography } from '@mui/material'
import { GridColumnMenuProps, useGridApiContext } from '@mui/x-data-grid-premium'
import { isNil } from 'lodash'
import { FiFilter } from 'react-icons/fi'
import { RxArrowDown, RxArrowUp, RxColumns, RxCrossCircled } from 'react-icons/rx'
import { TbSortAscendingLetters, TbSortDescendingLetters } from 'react-icons/tb'
import { DataType, IndexType } from '~/common/generated/apollo-operations'
import useStyles from '~/common/hooks/useStyles'
import { DetailedTableColumn, DetailedTableTabColumn, TableType } from '~/components/tables/types'
import { useTypedTranslation } from '../../../common/hooks/useTypedTranslation'
import { FreezeColumnMenuItem } from './FreezeColumnMenuItem'
import { TableMenuOptions, TableMenuTypes, useTableMenuContext } from './TableMenus'

export interface DossColumnLevelMenuProps extends GridColumnMenuProps {
  tableColumns: DetailedTableColumn[]
  selectedTableTabColumns: DetailedTableTabColumn[]
  tableType: TableType
  tableId: string
}

function getStyles() {
  return {
    sortMenuItem: {
      flexGrow: 1,
    },
  }
}

/**
 * Column level menu for the DossTable that allows for sorting, filtering, and selecting columns.
 */
export function DossColumnLevelMenu({
  tableColumns,
  selectedTableTabColumns,
  tableId,
  tableType,
  ...props
}: DossColumnLevelMenuProps) {
  const styles = useStyles(getStyles)
  const { openMenu } = useTableMenuContext()
  const apiRef = useGridApiContext()
  const { t } = useTypedTranslation()

  const column = tableColumns.find((column) => column.id === props.colDef.field)
  const systemColumnType = parseSystemColumnFieldId(props.colDef.field)
  const tableTabColumn = selectedTableTabColumns.find(
    (col) =>
      col.tableColumn?.id === props.colDef.field ||
      (systemColumnType !== null && col.systemGeneratedColumnType === systemColumnType),
  )
  const isColumnFrozen = apiRef.current.isColumnPinned(props.colDef.field)

  if (isNil(column) && systemColumnType === null) {
    return null
  }

  const sortModel = apiRef.current.getSortModel()
  const sorted = sortModel.some((sort) => sort.field === props.colDef.field)

  const getUnsortMenuItemContent = () => {
    return (
      <Stack
        alignItems="center"
        direction="row"
        display="flex"
        justifyContent="flex-start"
        spacing={1}
      >
        <RxCrossCircled />
        <Typography variant="caption">{t('table.unsort')}</Typography>
      </Stack>
    )
  }

  // Use DataType for switch (system columns map via getDataTypeForSystemColumn)
  const systemColumnDataType =
    systemColumnType !== null
      ? (getDataTypeForSystemGeneratedColumn(systemColumnType).dataType as DataType)
      : DataType.STRING

  const columnType = column?.type ?? systemColumnDataType
  const indexType = column?.indexType ?? IndexType.NONE

  const getAscSortMenuItemContent = () => {
    switch (columnType) {
      case DataType.STRING:
      case DataType.FORMULA_STRING:
      case DataType.ENUM:
      case DataType.FILE_URL:
      case DataType.USER:
      case DataType.LOOKUP: {
        return (
          <Stack alignItems="center" direction="row" display="flex">
            <TbSortAscendingLetters />
          </Stack>
        )
      }
      case DataType.ID: {
        if (indexType === IndexType.NUMBER) {
          return (
            <Stack
              alignItems="center"
              direction="row"
              display="flex"
              justifyContent="flex-start"
              spacing={1}
            >
              <RxArrowUp />
              <Typography variant="caption">{t('table.asc')}</Typography>
            </Stack>
          )
        } else {
          return (
            <Stack alignItems="center" direction="row" display="flex">
              <TbSortAscendingLetters />
            </Stack>
          )
        }
      }
      case DataType.NUMBER:
      case DataType.BOOLEAN:
      case DataType.DATE:
      case DataType.DATE_TIME:
      case DataType.ARRAY:
      case DataType.FORMULA_NUMBER:
      case DataType.FORMULA_DATE:
      case DataType.FORMULA_DATE_TIME: {
        return (
          <Stack
            alignItems="center"
            direction="row"
            display="flex"
            justifyContent="flex-start"
            spacing={1}
          >
            <RxArrowUp />
            <Typography variant="caption">{t('table.asc')}</Typography>
          </Stack>
        )
      }
      case DataType.FORMULA_BOOLEAN:
        // FORMULA_BOOLEAN is out of scope for the non-numeric formulas project
        return null
    }
  }

  const getDescSortMenuItemContent = () => {
    switch (columnType) {
      case DataType.STRING:
      case DataType.FORMULA_STRING:
      case DataType.ENUM:
      case DataType.FILE_URL:
      case DataType.USER:
      case DataType.LOOKUP: {
        return (
          <Stack alignItems="center" direction="row" display="flex">
            <TbSortDescendingLetters />
          </Stack>
        )
      }
      case DataType.ID: {
        if (indexType === IndexType.NUMBER) {
          return (
            <Stack
              alignItems="center"
              direction="row"
              display="flex"
              justifyContent="flex-start"
              spacing={1}
            >
              <RxArrowDown />
              <Typography variant="caption">{t('table.desc')}</Typography>
            </Stack>
          )
        } else {
          return (
            <Stack alignItems="center" direction="row" display="flex">
              <TbSortDescendingLetters />
            </Stack>
          )
        }
      }
      case DataType.NUMBER:
      case DataType.BOOLEAN:
      case DataType.DATE:
      case DataType.DATE_TIME:
      case DataType.ARRAY:
      case DataType.FORMULA_NUMBER:
      case DataType.FORMULA_DATE:
      case DataType.FORMULA_DATE_TIME: {
        return (
          <Stack
            alignItems="center"
            direction="row"
            display="flex"
            justifyContent="flex-start"
            spacing={1}
          >
            <RxArrowDown />
            <Typography variant="caption">{t('table.desc')}</Typography>
          </Stack>
        )
      }
      case DataType.FORMULA_BOOLEAN:
        // FORMULA_BOOLEAN is out of scope for the non-numeric formulas project
        return null
    }
  }

  return (
    <>
      <Stack direction="column">
        <Stack direction="row" justifyContent="space-between">
          <MenuItem
            onClick={(e) => {
              openMenu(
                TableMenuTypes.COLUMN,
                TableMenuOptions.SORT,
                { ...props, sortDirection: 'asc' },
                apiRef,
              )
              props.hideMenu(e)
            }}
            sx={styles.sortMenuItem}
          >
            {getAscSortMenuItemContent()}
          </MenuItem>
          <MenuItem
            onClick={(e) => {
              openMenu(
                TableMenuTypes.COLUMN,
                TableMenuOptions.SORT,
                { ...props, sortDirection: 'desc' },
                apiRef,
              )
              props.hideMenu(e)
            }}
            sx={styles.sortMenuItem}
          >
            {getDescSortMenuItemContent()}
          </MenuItem>
        </Stack>
        {sorted && (
          <Stack direction="row" justifyContent="space-between">
            <MenuItem
              onClick={(e) => {
                openMenu(
                  TableMenuTypes.COLUMN,
                  TableMenuOptions.SORT,
                  { ...props, sortDirection: 'delete' },
                  apiRef,
                )
                props.hideMenu(e)
              }}
              sx={styles.sortMenuItem}
            >
              {getUnsortMenuItemContent()}
            </MenuItem>
          </Stack>
        )}

        <MenuItem
          onClick={(e) => {
            openMenu(TableMenuTypes.COLUMN, TableMenuOptions.FILTER, props, apiRef)
            props.hideMenu(e)
          }}
        >
          <Stack
            alignItems="center"
            direction="row"
            display="flex"
            justifyContent="flex-start"
            spacing={1}
          >
            <FiFilter />
            <Typography variant="caption">{t('table.filter')}</Typography>
          </Stack>
        </MenuItem>

        <MenuItem>
          <Stack
            alignItems="center"
            direction="row"
            display="flex"
            justifyContent="flex-start"
            onClick={(e) => {
              openMenu(TableMenuTypes.COLUMN, TableMenuOptions.SELECT_COLUMNS, props, apiRef)
              props.hideMenu(e)
            }}
            spacing={1}
          >
            <RxColumns />
            <Typography variant="caption">{t('table.select_columns')}</Typography>
          </Stack>
        </MenuItem>
        {tableTabColumn && (
          <FreezeColumnMenuItem
            isColumnFrozen={!!isColumnFrozen}
            tableTabColumn={tableTabColumn}
            {...props}
          />
        )}
      </Stack>
    </>
  )
}
